/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  HostListener,
  inject,
  input,
  Input,
  TemplateRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AttachDialogMode } from '@app/cluster-management/dialog-attach-cluster/types';
import { targetSelector } from '@app/core/ngrx';
import { nebulaSubscription } from '@app/core/ngrx/nebula-plans.selectors';
import { AppState } from '@app/core/types';
import { isSubscriptionUnpaid } from '@app/domain/nebula';
import { shareReplayLastWithRefCount } from '@common/rxjs-operators/share-replay-last-with-ref-count';
import { Store } from '@ngrx/store';
import { NebulaSubscriptionInfo } from '@shared/types/nebula';
import { Angulartics2, Angulartics2Module } from 'angulartics2';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { ShowForTargetDirective } from '../../modules/ggcc-target/show-for-target.directive';
import { MapPipe } from '../../pipes/map.pipe';

type AttachClusterButtonContext = {
  routerLink$: Observable<string | any[] | null | undefined>;
  disabled$: Observable<boolean>;
};

@Directive({
  selector: 'ng-template[attachClusterButtonTemplate]',
  standalone: true,
})
export class AttachClusterButtonTemplateDirective {
  static ngTemplateContextGuard(
    dir: AttachClusterButtonTemplateDirective,
    ctx: unknown,
  ): ctx is AttachClusterButtonContext {
    return true;
  }
}

@Component({
  selector: 'attach-cluster-button',
  templateUrl: 'attach-cluster-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltipModule,
    NgTemplateOutlet,
    ShowForTargetDirective,
    MatButtonModule,
    RouterLink,
    AsyncPipe,
    MapPipe,
    Angulartics2Module,
  ],
})
export class AttachClusterButtonComponent {
  private store = inject(Store<AppState>);
  private angulartics2 = inject(Angulartics2);

  @HostListener('click', ['$event'])
  onClick(event: PointerEvent) {
    this.angulartics2.eventTrack.next({
      action: 'AttachCluster',
      properties: {
        category: 'ButtonClick',
        eventType: 'click',
      },
    });
  }

  public readonly product = input.required<AttachDialogMode>();

  @Input()
  text: string = 'Attach cluster';

  @Input()
  type: 'raised' | 'stroked' = 'stroked';

  @ContentChild(AttachClusterButtonTemplateDirective, { read: TemplateRef })
  template?: TemplateRef<AttachClusterButtonContext>;

  unpaidTooltip = 'Please resolve payment issues to attach new clusters';

  nebulaSubscription$ = this.store.select(nebulaSubscription);

  disabled$: Observable<boolean> = this.store.select(targetSelector).pipe(
    switchMap((target) => {
      return target === 'on-premise'
        ? of(false)
        : this.nebulaSubscription$.pipe(
            filter(Boolean),
            map((subscription) => isSubscriptionUnpaid(subscription)),
            startWith(true),
          );
    }),
    shareReplayLastWithRefCount,
  );

  public routerLink$ = combineLatest([this.disabled$, toObservable(this.product)]).pipe(
    map(([disabled, product]) => (disabled ? null : ['/', { outlets: { dialog: ['attach', product] } }])),
  );

  isUnpaid(subscription: NebulaSubscriptionInfo | null): boolean {
    return isSubscriptionUnpaid(subscription);
  }
}
